import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
} from '@mui/material';

import ContentArea from '../../components/contentArea';
import Button from '../../components/button';
import Dropdown from '../../components/dropdown';
import Toggle from '../../components/toggle';
import { SortableHeaderCell, HeaderCell } from '../../components/table/headerCell';
import { useTranslation } from 'react-i18next';
import { getTableStyles } from '../../components/table/styles';
import { createSortConfig, handleSort, formatDateTimeDisplay, toDisplayString } from '../../utils/base';
import { eventAPI } from '../../actions/imsServer';
import { EVENT_TYPES, API_PAGE_SIZE } from '../../constants';
import { Prev, Next } from '../../assets/images';

const EventAdmin = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterType, setFilterType] = useState('all');
    const [progressFilter, setProgressFilter] = useState(1); // 0: 완료, 1: 진행중
    const [sortConfig, setSortConfig] = useState(createSortConfig('startDate', 'desc'));
    const [currentPage, setCurrentPage] = useState(1);
    const [total, setTotal] = useState(0); // 서버에서 받은 전체 개수
    const ITEMS_PER_PAGE = API_PAGE_SIZE;

    const fetchData = async (page = 1, currentProgressFilter = progressFilter, currentFilterType = filterType, currentSortConfig = sortConfig) => {
        try {
            setLoading(true);
            setError(null);

            const currentOffset = (page - 1) * ITEMS_PER_PAGE;

            const eventParams = {
                expand: 'user',
                offset: currentOffset,
                limit: ITEMS_PER_PAGE
            };

            // progressFilter에 따라 status로 서버 사이드 필터링
            if (currentProgressFilter === 0) {
                // 완료: finishing만
                eventParams.status = 'finishing';
            } else {
                // 진행중: waiting, ongoing 둘 다
                eventParams.status = 'waiting,ongoing';
            }

            // 서버사이드 정렬 파라미터 추가
            if (currentSortConfig.key) {
                eventParams.sort = currentSortConfig.key;
                eventParams.order = currentSortConfig.direction === 'asc' ? 'asc' : 'desc';
            }

            // 서버사이드 필터링 파라미터 추가
            if (currentFilterType === 'all') {
                // 전체는 필터링 없음
            } else {
                const eventType = EVENT_TYPES.find(type => type.label === currentFilterType);
                if (eventType) {
                    eventParams.type = String(eventType.value);
                }
            }

            const eventsRes = await eventAPI.getEvents(eventParams);
            const events = eventsRes?.data?.events || eventsRes?.events || [];
            const totalCount = eventsRes?.data?.total || eventsRes?.total || 0;
            setTotal(totalCount);

            const rows = events.map(e => {
                // status로 진행유무 판단
                let progress = '-';
                const status = e.status;

                if (status === 'waiting') {
                    progress = t('event_status_before_start');
                } else if (status === 'ongoing') {
                    progress = t('event_status_ongoing');
                } else if (status === 'finishing') {
                    progress = t('completed');
                }

                return {
                    id: e.id,
                    startDate: e.startDate,
                    endDate: e.endDate,
                    name: e.name || '-',
                    type: e.type,
                    progress,
                    state: e.state !== undefined ? e.state : 0,
                    totalParticipationUsedPoint: e.totalParticipationUsedPoint || 0,
                    userName: e.user?.name || '-',
                };
            });

            setEvents(rows);
        } catch (err) {
            console.error('Failed to fetch events:', err);
            setError(t('failed_to_load_event_list'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
        setEvents([]);
        fetchData(1, progressFilter, filterType, sortConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData는 매 렌더 새 참조라 의존성에서 제외
    }, [progressFilter, filterType, sortConfig]);

    // 페이지 변경 시 데이터 다시 가져오기
    useEffect(() => {
        if (currentPage > 1) {
            fetchData(currentPage, progressFilter, filterType, sortConfig);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData는 매 렌더 새 참조라 의존성에서 제외
    }, [currentPage, progressFilter, filterType, sortConfig]);

    // 페이지 변경 핸들러
    const handlePageChange = useCallback((newPage) => {
        if (newPage < 1) return;
        const maxPage = Math.ceil(total / ITEMS_PER_PAGE);
        if (newPage > maxPage && maxPage > 0) return;

        setCurrentPage(newPage);
    }, [total]);

    // 이전 페이지
    const handlePrevPage = useCallback(() => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    }, [currentPage, handlePageChange]);

    // 다음 페이지
    const handleNextPage = useCallback(() => {
        const maxPage = Math.ceil(total / ITEMS_PER_PAGE);
        if (currentPage < maxPage) {
            handlePageChange(currentPage + 1);
        }
    }, [currentPage, total, handlePageChange]);

    const onSort = (key) => {
        const next = handleSort(sortConfig, key);
        setSortConfig(next);
        // 정렬 시 첫 페이지로 리셋
        setCurrentPage(1);
        // 정렬 변경 시 데이터 다시 가져오기
        fetchData(1, progressFilter, filterType, next);
    };

    const handleCreate = () => {
        navigate('/events/edit');
    };

    const handleRowClick = (event) => {
        // adminDetail로 이동하면서 이벤트 데이터 전달
        navigate('/events/admin-detail', {
            state: {
                eventData: {
                    id: event.id,
                    name: event.name,
                    type: event.type,
                    startDate: event.startDate,
                    endDate: event.endDate,
                }
            }
        });
    };

    return (
        <ContentArea>
            {/* 검색/필터 영역 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', px: 2.5, py: 2.5, mb: 3, gap: 2 }}>
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <Dropdown
                            options={[
                                { value: 'all', label: 'all' },
                                ...EVENT_TYPES.map(type => ({ value: type.label, label: type.label }))
                            ]}
                            value={filterType}
                            onChange={(option) => {
                                setFilterType(option.value);
                                // 필터 변경 시 첫 페이지로 리셋
                                setCurrentPage(1);
                                // 필터 변경 시 데이터 다시 가져오기
                                fetchData(1, progressFilter, option.value, sortConfig);
                            }}
                            placeholder={t('select_filter')}
                        />
                    </Box>
                    <Toggle
                        options={[t('completed'), t('event_status_ongoing')]}
                        value={progressFilter}
                        onChange={(index) => {
                            setProgressFilter(index);
                            // 진행 상태 필터 변경 시 첫 페이지로 리셋하고 데이터 재로드
                            setCurrentPage(1);
                            setEvents([]);
                            fetchData(1, index, filterType, sortConfig);
                        }}
                        sx={{ width: 'auto', minWidth: '200px' }}
                    />
                </Box>

            </Box>

            {/* 에러 */}
            {error && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            )}

            {/* 테이블 */}
            {!loading && (
                <Box sx={{ px: 2.5, mb: 3 }}>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <SortableHeaderCell columnKey="startDate" label={t('event_post_start_date')} sortConfig={sortConfig} onSort={onSort} align="center" />
                                    <SortableHeaderCell columnKey="endDate" label={t('event_post_end_date')} sortConfig={sortConfig} onSort={onSort} align="center" />
                                    <SortableHeaderCell columnKey="name" label={t('title')} sortConfig={sortConfig} onSort={onSort} align="center" />
                                    <SortableHeaderCell columnKey="type" label={t('event_type')} sortConfig={sortConfig} onSort={onSort} align="center" />
                                    <HeaderCell label={t('total_application_point')} align="center" />
                                    <HeaderCell label={t('author')} align="center" />
                                    <HeaderCell label={t('progress_status')} align="center" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.isArray(events) && events.length > 0 ? events.map((e, idx) => (
                                    <TableRow
                                        key={e.id || idx}
                                        onClick={() => handleRowClick(e)}
                                        sx={{
                                            ...getTableStyles(theme).tableRowStyle,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: theme.palette.figma.white[5],
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            {e.startDate ? formatDateTimeDisplay(e.startDate, 'datetime') : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {e.endDate ? formatDateTimeDisplay(e.endDate, 'datetime') : '-'}
                                        </TableCell>
                                        <TableCell>{e.name}</TableCell>
                                        <TableCell>{(() => { const typ = EVENT_TYPES.find(type => type.value === e.type); return typ ? toDisplayString(t(typ.label), typ.label) : '-'; })()}</TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>
                                            {e.totalParticipationUsedPoint?.toLocaleString() || '0'}
                                        </TableCell>
                                        <TableCell>{e.userName}</TableCell>
                                        <TableCell>{e.progress}</TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                            {loading ? t('loading_data') : t('no_event_data')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* 페이징 컨트롤 */}
                    {total > 0 && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '20px 0',
                        }}>
                            <Box
                                onClick={handlePrevPage}
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: currentPage > 1 ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: currentPage > 1 ? 1 : 0.3,
                                }}
                            >
                                <Prev color={theme.palette.figma?.white?.[80] || 'rgba(255, 255, 255, 0.8)'} size={20} />
                            </Box>
                            <Typography sx={{
                                fontSize: '16px',
                                fontWeight: 500,
                                color: theme.palette.figma?.white?.[80] || 'rgba(255, 255, 255, 0.8)',
                                letterSpacing: '-0.48px',
                                whiteSpace: 'nowrap'
                            }}>
                                {currentPage} / {Math.ceil(total / ITEMS_PER_PAGE)}
                            </Typography>
                            <Box
                                onClick={handleNextPage}
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: currentPage < Math.ceil(total / ITEMS_PER_PAGE) ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: currentPage < Math.ceil(total / ITEMS_PER_PAGE) ? 1 : 0.3,
                                }}
                            >
                                <Next color={theme.palette.figma?.white?.[80] || 'rgba(255, 255, 255, 0.8)'} size={20} />
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {/* 하단 생성 버튼 */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2.5, pb: 3, alignSelf: 'stretch' }}>
                <Button onClick={handleCreate}>{t('create_event')}</Button>
            </Box>
        </ContentArea>
    );
};

export { EventAdmin };


